<script>
import Layout from '@/layouts/main.vue';
import { ehAdmin } from '@/helpers/permissao';
import PageHeader from '@/components/page-header';
import { api, sucesso, erro, confirmar } from '@/helpers/common/api';
import { carregarMarca, assetUrl, marcaState } from '@/services/marca';
import logoHorizontalPadrao from '@/assets/images/logo-pcdob-horizontal.png';
import logoIconePadrao from '@/assets/images/logo-pcdob-icon.png';

const PADRAO_CORES = {
  cor_primaria: '#971219',
  cor_primaria_dark: '#3f060a',
  cor_acento: '#fff100',
  cor_acento_dark: '#f2c94c',
};

// Definição das imagens gerenciáveis (chave no banco + rótulos + preview padrão).
const IMAGENS = [
  { chave: 'logo_horizontal', titulo: 'Logo horizontal', ajuda: 'Exibida na topbar, sidebar e login.', padrao: logoHorizontalPadrao, fundo: 'brand' },
  { chave: 'logo_icone', titulo: 'Ícone / logo compacta', ajuda: 'Versão quadrada usada na topbar recolhida.', padrao: logoIconePadrao, fundo: 'brand' },
  { chave: 'favicon', titulo: 'Favicon', ajuda: 'Ícone da aba do navegador (.ico, .png ou .svg).', padrao: '/favicon.svg', fundo: 'claro' },
  { chave: 'pwa_icon_192', titulo: 'Ícone PWA 192', ajuda: 'Ícone do app instalado (192×192 .png).', padrao: '/img/icons/android-chrome-192x192.png', fundo: 'claro' },
  { chave: 'pwa_icon_512', titulo: 'Ícone PWA 512', ajuda: 'Ícone/splash do app instalado (512×512 .png).', padrao: '/img/icons/android-chrome-512x512.png', fundo: 'claro' },
];

export default {
  name: 'MarcaCustomizacao',
  components: { Layout, PageHeader },
  data() {
    return {
      IMAGENS,
      carregando: true,
      salvando: false,
      form: {
        nome_sistema: '',
        nome_curto: '',
        nome_exibicao: '',
        seo_descricao: '',
        twitter_site: '',
        ...PADRAO_CORES,
      },
      // Imagem de compartilhamento (OG/Twitter) — usa os mesmos helpers de upload.
      ogItem: {
        chave: 'og_imagem',
        titulo: 'Imagem de compartilhamento',
        ajuda: 'og:image / twitter:image — recomendado 1200×630.',
        padrao: logoHorizontalPadrao,
        fundo: 'claro',
      },
      flags: {}, // tem_<chave> vindo do servidor
      // Alterações de imagem: undefined = sem mudança; null = limpar; string = novo data URI.
      imgChanges: {},
      versao: 0, // usado pra bustar cache das previews
    };
  },
  computed: {
    corInvalida() {
      return Object.keys(PADRAO_CORES).some((k) => !/^#[0-9a-fA-F]{3,8}$/.test(this.form[k] || ''));
    },
  },
  async created() {
    const user = JSON.parse(localStorage.getItem('userdata') || '{}');
    if (!ehAdmin(user)) {
      this.$router.replace('/');
      return;
    }
    await this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const data = await api.read('/marca');
        if (data && typeof data === 'object') {
          this.form.nome_sistema = data.nome_sistema || 'PCdoB CRM';
          this.form.nome_curto = data.nome_curto || 'PCdoB';
          this.form.nome_exibicao = data.nome_exibicao || 'PC do B';
          this.form.seo_descricao = data.seo_descricao || '';
          this.form.twitter_site = data.twitter_site || '';
          Object.keys(PADRAO_CORES).forEach((k) => { this.form[k] = data[k] || PADRAO_CORES[k]; });
          this.flags = {
            logo_horizontal: !!data.tem_logo_horizontal,
            logo_icone: !!data.tem_logo_icone,
            favicon: !!data.tem_favicon,
            pwa_icon_192: !!data.tem_pwa_icon_192,
            pwa_icon_512: !!data.tem_pwa_icon_512,
            og_imagem: !!data.tem_og_imagem,
          };
          this.versao = data.updated_at ? new Date(data.updated_at).getTime() : 0;
        }
      } finally {
        this.imgChanges = {};
        this.carregando = false;
      }
    },

    // Preview de uma imagem considerando alterações pendentes.
    previewImagem(item) {
      const alterado = this.imgChanges[item.chave];
      if (typeof alterado === 'string') return alterado; // novo data URI
      if (alterado === null) return item.padrao; // limpo → volta ao padrão
      if (this.flags[item.chave]) return `${assetUrl(item.chave)}&t=${this.versao}`;
      return item.padrao;
    },

    usandoPadrao(item) {
      const alterado = this.imgChanges[item.chave];
      if (typeof alterado === 'string') return false;
      if (alterado === null) return true;
      return !this.flags[item.chave];
    },

    onArquivo(item, evt) {
      const file = evt.target.files && evt.target.files[0];
      evt.target.value = ''; // permite reescolher o mesmo arquivo
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        erro({ title: 'Selecione um arquivo de imagem.' });
        return;
      }
      if (file.size > 3 * 1024 * 1024) {
        erro({ title: 'Imagem muito grande', text: 'O limite é 3 MB por imagem.' });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => { this.imgChanges[item.chave] = String(reader.result); };
      reader.readAsDataURL(file);
    },

    limparImagem(item) {
      // Se já não havia imagem custom, não há o que limpar.
      if (!this.flags[item.chave] && this.imgChanges[item.chave] === undefined) return;
      this.imgChanges[item.chave] = null;
    },

    corPreview(cor, fallback) {
      return /^#[0-9a-fA-F]{3,8}$/.test(cor || '') ? cor : fallback;
    },

    async salvar() {
      if (this.corInvalida) {
        erro({ title: 'Cor inválida', text: 'Use o formato hexadecimal, ex.: #971219.' });
        return;
      }
      this.salvando = true;
      try {
        const payload = {
          nome_sistema: this.form.nome_sistema.trim() || 'PCdoB CRM',
          nome_curto: this.form.nome_curto.trim() || 'PCdoB',
          nome_exibicao: this.form.nome_exibicao.trim() || 'PC do B',
          seo_descricao: this.form.seo_descricao.trim(),
          twitter_site: this.form.twitter_site.trim(),
          ...Object.fromEntries(Object.keys(PADRAO_CORES).map((k) => [k, this.form[k]])),
        };
        // Só envia imagens que mudaram (novo data URI ou limpar=null).
        Object.keys(this.imgChanges).forEach((chave) => {
          if (this.imgChanges[chave] !== undefined) payload[chave] = this.imgChanges[chave];
        });

        const result = await api.update('/marca/salvar', payload);
        if (result === false) {
          erro({ title: 'Não foi possível salvar', text: 'Verifique se você tem permissão de administrador.' });
          return;
        }
        // Re-aplica a marca em runtime (cores/logo/nome/favicon/manifest) e recarrega o form.
        await carregarMarca();
        await this.carregar();
        sucesso({ title: 'Marca atualizada!' });
      } finally {
        this.salvando = false;
      }
    },

    restaurarPadrao() {
      confirmar({
        options: {
          title: 'Restaurar marca padrão?',
          text: 'Volta às cores e à marca padrão e remove logo/ícones customizados. Não pode ser desfeito.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, restaurar',
          customClass: {
            confirmButton: 'btn btn-danger w-xs me-2',
            cancelButton: 'btn btn-secondary w-xs',
          },
          buttonsStyling: false,
        },
        confirmado: () => {
          this.form.nome_sistema = 'PCdoB CRM';
          this.form.nome_curto = 'PCdoB';
          this.form.nome_exibicao = 'PC do B';
          this.form.seo_descricao = 'Sistema Gerencial PC do B';
          this.form.twitter_site = '';
          Object.assign(this.form, PADRAO_CORES);
          // Marca todas as imagens (inclusive a de compartilhamento) para limpeza.
          IMAGENS.forEach((item) => { this.imgChanges[item.chave] = null; });
          this.imgChanges.og_imagem = null;
          this.salvar();
        },
      });
    },

    marcaAtualNome() {
      return marcaState.nome_curto || 'PCdoB';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Marca / Aparência" pageTitle="Customização" />

    <div v-if="carregando" class="text-center py-5">
      <BSpinner variant="primary" />
    </div>

    <BRow v-else>
      <!-- Coluna do formulário -->
      <BCol xl="8">
        <!-- Identidade -->
        <BCard no-body>
          <div class="card-header">
            <h5 class="card-title mb-0"><i class="ri-price-tag-3-line align-bottom me-1"></i> Identidade</h5>
          </div>
          <div class="card-body">
            <BRow class="g-3">
              <BCol md="6">
                <label class="form-label">Nome do sistema</label>
                <input v-model="form.nome_sistema" type="text" class="form-control" placeholder="Ex.: PCdoB CRM" maxlength="150" />
                <small class="text-muted">Título da aba e nome do app instalado.</small>
              </BCol>
              <BCol md="6">
                <label class="form-label">Nome curto</label>
                <input v-model="form.nome_curto" type="text" class="form-control" placeholder="Ex.: PCdoB" maxlength="60" />
                <small class="text-muted">Usado como short_name do PWA e alt das logos.</small>
              </BCol>
              <BCol md="6">
                <label class="form-label">Nome de exibição</label>
                <input v-model="form.nome_exibicao" type="text" class="form-control" placeholder="Ex.: PC do B" maxlength="150" />
                <small class="text-muted">Aparece em referências fixas, como o copyright do rodapé.</small>
              </BCol>
            </BRow>
          </div>
        </BCard>

        <!-- Cores -->
        <BCard no-body>
          <div class="card-header">
            <h5 class="card-title mb-0"><i class="ri-palette-line align-bottom me-1"></i> Cores do tema</h5>
          </div>
          <div class="card-body">
            <BRow class="g-4">
              <BCol md="6">
                <label class="form-label">Cor primária (claro)</label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="form.cor_primaria" type="color" class="form-control form-control-color" />
                  <input v-model="form.cor_primaria" type="text" class="form-control" maxlength="9" />
                </div>
                <small class="text-muted">Barras, botões e destaques no modo claro.</small>
              </BCol>
              <BCol md="6">
                <label class="form-label">Cor primária (escuro)</label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="form.cor_primaria_dark" type="color" class="form-control form-control-color" />
                  <input v-model="form.cor_primaria_dark" type="text" class="form-control" maxlength="9" />
                </div>
                <small class="text-muted">Barras no modo escuro.</small>
              </BCol>
              <BCol md="6">
                <label class="form-label">Cor de acento (claro)</label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="form.cor_acento" type="color" class="form-control form-control-color" />
                  <input v-model="form.cor_acento" type="text" class="form-control" maxlength="9" />
                </div>
                <small class="text-muted">Texto/ícones sobre as barras (ex.: amarelo).</small>
              </BCol>
              <BCol md="6">
                <label class="form-label">Cor de acento (escuro)</label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="form.cor_acento_dark" type="color" class="form-control form-control-color" />
                  <input v-model="form.cor_acento_dark" type="text" class="form-control" maxlength="9" />
                </div>
                <small class="text-muted">Texto/ícones sobre as barras no modo escuro.</small>
              </BCol>
            </BRow>
          </div>
        </BCard>

        <!-- Imagens -->
        <BCard no-body>
          <div class="card-header">
            <h5 class="card-title mb-0"><i class="ri-image-line align-bottom me-1"></i> Logo e ícones</h5>
          </div>
          <div class="card-body">
            <div v-for="item in IMAGENS" :key="item.chave" class="d-flex align-items-center gap-3 py-2 border-bottom flex-wrap">
              <div class="marca-thumb" :class="item.fundo === 'brand' ? 'marca-thumb--brand' : 'marca-thumb--claro'">
                <img :src="previewImagem(item)" :alt="item.titulo" />
              </div>
              <div class="flex-grow-1" style="min-width: 180px;">
                <div class="fw-medium">{{ item.titulo }}
                  <span v-if="usandoPadrao(item)" class="badge bg-secondary-subtle text-secondary ms-1">Padrão</span>
                  <span v-else class="badge bg-success-subtle text-success ms-1">Personalizado</span>
                </div>
                <small class="text-muted">{{ item.ajuda }}</small>
              </div>
              <div class="d-flex gap-2">
                <label class="btn btn-soft-primary btn-sm mb-0">
                  <i class="ri-upload-2-line align-bottom"></i> Enviar
                  <input type="file" accept="image/*" class="d-none" @change="onArquivo(item, $event)" />
                </label>
                <BButton variant="soft-danger" size="sm" :disabled="usandoPadrao(item)" @click="limparImagem(item)">
                  <i class="ri-delete-bin-line"></i>
                </BButton>
              </div>
            </div>
            <small class="text-muted d-block mt-2">
              <i class="ri-information-line"></i>
              Ícones/splash do PWA já instalado só atualizam após reinstalar o app (limitação do sistema).
            </small>
          </div>
        </BCard>

        <!-- SEO / compartilhamento -->
        <BCard no-body>
          <div class="card-header">
            <h5 class="card-title mb-0"><i class="ri-share-forward-line align-bottom me-1"></i> SEO e compartilhamento</h5>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">Descrição (SEO)</label>
              <textarea v-model="form.seo_descricao" class="form-control" rows="2" maxlength="300" placeholder="Descrição do sistema para buscadores e redes sociais"></textarea>
              <small class="text-muted">Vira a meta description e as descrições de Open Graph (Facebook) e Twitter.</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Twitter / X (handle)</label>
              <input v-model="form.twitter_site" type="text" class="form-control" maxlength="50" placeholder="@suamarca" />
              <small class="text-muted">Opcional — usado em twitter:site.</small>
            </div>
            <label class="form-label d-block">Imagem de compartilhamento</label>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="marca-thumb marca-thumb--claro og-thumb">
                <img :src="previewImagem(ogItem)" alt="Imagem de compartilhamento" />
              </div>
              <div class="flex-grow-1" style="min-width: 180px;">
                <div class="fw-medium">
                  <span v-if="usandoPadrao(ogItem)" class="badge bg-secondary-subtle text-secondary">Padrão (logo)</span>
                  <span v-else class="badge bg-success-subtle text-success">Personalizada</span>
                </div>
                <small class="text-muted">{{ ogItem.ajuda }}</small>
              </div>
              <div class="d-flex gap-2">
                <label class="btn btn-soft-primary btn-sm mb-0">
                  <i class="ri-upload-2-line align-bottom"></i> Enviar
                  <input type="file" accept="image/*" class="d-none" @change="onArquivo(ogItem, $event)" />
                </label>
                <BButton variant="soft-danger" size="sm" :disabled="usandoPadrao(ogItem)" @click="limparImagem(ogItem)">
                  <i class="ri-delete-bin-line"></i>
                </BButton>
              </div>
            </div>
          </div>
        </BCard>

        <div class="d-flex gap-2 mb-4">
          <BButton variant="success" :disabled="salvando || corInvalida" @click="salvar">
            <BSpinner v-if="salvando" small class="me-1" />
            <i v-else class="ri-save-line align-bottom me-1"></i> Salvar alterações
          </BButton>
          <BButton variant="soft-secondary" :disabled="salvando" @click="restaurarPadrao">
            <i class="ri-refresh-line align-bottom me-1"></i> Restaurar Padrão
          </BButton>
        </div>
      </BCol>

      <!-- Coluna de preview -->
      <BCol xl="4">
        <BCard no-body class="marca-preview-card">
          <div class="card-header">
            <h5 class="card-title mb-0"><i class="ri-eye-line align-bottom me-1"></i> Prévia</h5>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-2">Modo claro</p>
            <div class="marca-bar" :style="{ background: corPreview(form.cor_primaria, '#971219') }">
              <img :src="previewImagem(IMAGENS[0])" alt="logo" class="marca-bar-logo" />
              <span class="marca-bar-dot" :style="{ background: corPreview(form.cor_acento, '#fff100') }"></span>
            </div>

            <p class="text-muted small mb-2 mt-4">Modo escuro</p>
            <div class="marca-bar" :style="{ background: corPreview(form.cor_primaria_dark, '#3f060a') }">
              <img :src="previewImagem(IMAGENS[0])" alt="logo" class="marca-bar-logo" />
              <span class="marca-bar-dot" :style="{ background: corPreview(form.cor_acento_dark, '#f2c94c') }"></span>
            </div>

            <div class="mt-4">
              <BButton variant="primary" size="sm" class="me-1">Botão primário</BButton>
              <span class="text-muted small">usa a cor primária (claro)</span>
            </div>
          </div>
        </BCard>
      </BCol>
    </BRow>
  </Layout>
</template>

<style scoped>
.marca-thumb {
  width: 84px;
  height: 56px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px solid var(--vz-border-color);
  flex: 0 0 auto;
}
.marca-thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
.og-thumb { width: 120px; height: 63px; }
.marca-thumb--brand { background: var(--brand-primary, #971219); }
.marca-thumb--claro { background: var(--vz-light, #f3f3f9); }

.marca-bar {
  height: 56px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}
.marca-bar-logo { max-height: 30px; max-width: 60%; object-fit: contain; }
.marca-bar-dot { width: 18px; height: 18px; border-radius: 50%; display: inline-block; }

.form-control-color { width: 3rem; min-width: 3rem; padding: 0.25rem; }
</style>
